// Libs
import React, { ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
// App
import styles from './page.module.scss';

import Page from '../../../../layout/shop-administration/page';
import PageHeader from '@/layout/shop-administration/page-header';
import { AuthConfig } from '../../../auth';
import { AUTHORITY } from 'app-shared/lib/api/user-access/account';
import { SHOP_ADMIN_PAGES_URLS } from '../../shop-administration.constants';
import { hasAuthority } from 'app-shared/lib/api/user-access/auth/auth.utils';
import TimeFilters, { TimeFilters as TimeFiltersData } from '../shared/time-filters';
import { buildCurrentPeriodQueryParams, buildInitialFilters } from '../statistics/page.builders';
import { Gutter } from 'app-shared/lib/layout/shared/gutter';
import {
  Avatar,
  Divider,
  FormControl,
  Icon,
  MenuItem,
  Select,
  SelectChangeEvent,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from 'ui-components';
import {
  BestSellerProductSummary,
  BestSellerProductsFilters,
  BestSellerProductsSortBy,
  useGetShopBestSellerProducts,
} from 'app-shared/lib/api/shop-administration/order/statistics';
import { formatPrice } from 'app-shared/lib/utils/display.utils';
import { useTableOneShotAnimation } from 'app-shared/lib/utils';
import Section from 'app-shared/lib/layout/shared/section';
import OrderPaymentTypeFilters from '../shared/order-payment-type-filters';

const DEFAULT_LIMIT = 50;

const SORT_LABELS: Record<BestSellerProductsSortBy, string> = {
  quantitySold: 'Quantités vendues',
  turnover: 'Chiffre d’affaires',
};

const BestSellersPage = (): ReactElement => {
  const router = useRouter();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: session } = useSession();
  const shopId = Number(router.query.shp_id);
  const { animated } = useTableOneShotAnimation();
  const initialFilters = buildInitialFilters();
  const [timeFilters, setTimeFilters] = useState<TimeFiltersData>(initialFilters);
  const [queryParams, setQueryParams] = useState<BestSellerProductsFilters>({
    ...buildCurrentPeriodQueryParams(initialFilters, isSmallScreen),
    sortBy: 'quantitySold',
    limit: DEFAULT_LIMIT,
  });
  const [hasExecutedGetBestSellerProducts, setHasExecutedGetBestSellerProducts] = useState(false);
  const {
    data: bestSellerProducts,
    isLoading: areBestSellerProductsLoading,
    execute: getBestSellerProducts,
    isExecuteReady,
  } = useGetShopBestSellerProducts(shopId);
  const hasBestSellerProducts = bestSellerProducts?.length > 0;

  useEffect(() => {
    const redirectToPleaseUpgradeIfUserNotAdmin = () => {
      if (session && !hasAuthority(session, AUTHORITY.SHOP_ORDER_ADMIN)) {
        router.push(SHOP_ADMIN_PAGES_URLS['please-upgrade']);
      }
    };

    redirectToPleaseUpgradeIfUserNotAdmin();
  }, [session]);

  useEffect(() => {
    if (isExecuteReady) {
      getBestSellerProducts(null, queryParams).then(() => setHasExecutedGetBestSellerProducts(true));
    }
  }, [isExecuteReady, queryParams]);

  const handleTimeFiltersChange = (updatedTimeFilters: TimeFiltersData) => {
    setTimeFilters(updatedTimeFilters);
    setQueryParams({
      ...queryParams,
      ...buildCurrentPeriodQueryParams(updatedTimeFilters, isSmallScreen),
    });
  };

  const handleSortChange = (event: SelectChangeEvent<BestSellerProductsSortBy>) => {
    setQueryParams({
      ...queryParams,
      sortBy: event.target.value as BestSellerProductsSortBy,
    });
  };

  const handleOrderPaymentTypeFiltersChange = (isPaidOnline: boolean) => {
    setQueryParams({
      ...queryParams,
      isPaidOnline,
    });
  };

  if (!session) return null;

  return (
    <Page>
      <PageHeader title="Meilleures ventes" />
      <Gutter autoMargin={false}>
        <TimeFilters filters={timeFilters} onFiltersChange={handleTimeFiltersChange} />
        <Stack mt={1} direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <SortFilter value={queryParams.sortBy || 'quantitySold'} onChange={handleSortChange} />
          <OrderPaymentTypeFilters
            initialValue={queryParams.isPaidOnline}
            onFiltersChange={handleOrderPaymentTypeFiltersChange}
          />
        </Stack>
        <Stack mt={1}>
          <Divider />
        </Stack>
        <Stack>
          {(!hasExecutedGetBestSellerProducts || areBestSellerProductsLoading) && (
            <Stack justifyContent="center" alignItems="center" mt={4} width="100%">
              <Spinner />
            </Stack>
          )}
          {hasExecutedGetBestSellerProducts && !areBestSellerProductsLoading && hasBestSellerProducts && (
            <BestSellerProductsTable products={bestSellerProducts || []} animated={animated} />
          )}
          {hasExecutedGetBestSellerProducts && !areBestSellerProductsLoading && !hasBestSellerProducts && (
            <Section spacing="medium">
              <Typography variant="body2">Aucune vente n&apos;a été trouvée pour la période sélectionnée.</Typography>
            </Section>
          )}
        </Stack>
      </Gutter>
    </Page>
  );
};

const SortFilter = (props: {
  value: BestSellerProductsSortBy;
  onChange: (event: SelectChangeEvent<BestSellerProductsSortBy>) => void;
}): ReactElement => {
  const { value, onChange } = props;

  return (
    <Stack width={250}>
      <FormControl color="secondary" size="small">
        <Select
          value={value}
          onChange={onChange}
          size="small"
          renderValue={(selected: BestSellerProductsSortBy) => (
            <Typography variant="body2" color="textPrimary" fontWeight={500}>
              Tri : {SORT_LABELS[selected]}
            </Typography>
          )}
        >
          {(Object.keys(SORT_LABELS) as BestSellerProductsSortBy[]).map((sortBy: BestSellerProductsSortBy) => (
            <MenuItem key={sortBy} value={sortBy}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} width="100%">
                <Typography variant="body2" color="textPrimary" fontWeight={500}>
                  {SORT_LABELS[sortBy]}
                </Typography>
                {value === sortBy && <Icon name="check" size="small" color="tertiary" />}
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

const BestSellerProductsTable = (props: {
  products: BestSellerProductSummary[];
  animated: boolean;
}): ReactElement => {
  const { products, animated } = props;

  return (
    <TableContainer>
      <Table className={styles.table} size="small" aria-label="tableau meilleures ventes" animated={animated}>
        <TableHead>
          <TableRow>
            <TableCell className={styles.firstCell}>
              <Typography variant="caption" fontWeight={600}>
                Rang
              </Typography>
            </TableCell>
            <TableCell align="left">
              <Typography variant="caption" fontWeight={600}>
                Produit
              </Typography>
            </TableCell>
            <TableCell align="left">
              <Typography variant="caption" fontWeight={600}>
                Catégorie
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="caption" fontWeight={600}>
                Quantité
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="caption" fontWeight={600}>
                Chiffre d’affaires
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="caption" fontWeight={600}>
                Prix moyen
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product: BestSellerProductSummary, index: number) => (
            <BestSellerProductRow key={product.productId} product={product} rank={index + 1} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BestSellerProductRow = (props: { product: BestSellerProductSummary; rank: number }): ReactElement => {
  const { product, rank } = props;

  return (
    <TableRow>
      <TableCell component="th" scope="row" className={styles.firstCell}>
        <Typography variant="body2" fontWeight={600}>
          #{rank}
        </Typography>
      </TableCell>
      <TableCell align="left">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar
            src={product.productImageUrl}
            variant="rounded"
            className={styles.productImage}
            alt={product.productName}
          >
            <Icon name="package" size="small" />
          </Avatar>
          <Typography variant="body2" fontWeight={600}>
            {product.productName}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="left">
        <Typography variant="body2">{product.shopProductCategoryName || '-'}</Typography>
      </TableCell>
      <TableCell align="right">{product.quantitySold}</TableCell>
      <TableCell align="right">{formatPrice(product.turnover, '0 €')}</TableCell>
      <TableCell align="right">{formatPrice(product.averageUnitPrice, '0 €')}</TableCell>
    </TableRow>
  );
};

const authConfig: AuthConfig = {
  restrictedAccess: true,
  authorizedAuthorities: [AUTHORITY.SHOP_ADMIN, AUTHORITY.GLOBAL_ADMIN],
};

BestSellersPage.authConfig = authConfig;

export default BestSellersPage;
